import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  Channel,
  ChannelBusinessProfileResponse,
  ChannelHealthService,
  ConfirmationComponent,
  UtilHelper,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';

/** Meta's documented business-profile verticals. */
const VERTICALS = [
  'UNDEFINED', 'OTHER', 'AUTO', 'BEAUTY', 'APPAREL', 'EDU', 'ENTERTAIN', 'EVENT_PLAN',
  'FINANCE', 'GROCERY', 'GOVT', 'HOTEL', 'HEALTH', 'NONPROFIT', 'PROF_SERVICES',
  'RETAIL', 'TRAVEL', 'RESTAURANT',
];

/** Meta profile field limits (RF-32) — mirrored server-side. */
const LIMITS = { about: 139, description: 512, address: 256, email: 128, websites: 2, websiteLength: 256 };

const PHOTO_TARGET_SIZE = 640;

/**
 * "Perfil do WhatsApp" area (SPEC-2026-08-channel-health, T-24 — RF-31…RF-35).
 * The one read-write area: edits go straight to the provider. Admin-only (RF-36 —
 * enforced by the shell and again by the API). A failed save keeps the edits (AC-29).
 */
@Component({
    selector: 'health-profile-tab',
    templateUrl: './health-profile-tab.component.html',
    styleUrl: './health-profile-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HealthProfileTabComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input({ required: true }) channel: Channel;
  @Input() refreshTick: number = 0;

  form: FormGroup;
  limits = LIMITS;
  verticals = VERTICALS;

  photoUrl?: string;
  saving: boolean = false;
  uploadingPhoto: boolean = false;
  requestingName: boolean = false;
  newDisplayName: string = '';

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    private channelHealthService: ChannelHealthService,
  ) {
    super(injector);
    this.form = this.formBuilder.group({
      about: ['', [Validators.maxLength(LIMITS.about)]],
      description: ['', [Validators.maxLength(LIMITS.description)]],
      address: ['', [Validators.maxLength(LIMITS.address)]],
      email: ['', [Validators.maxLength(LIMITS.email)]],
      vertical: [''],
      website1: ['', [Validators.maxLength(LIMITS.websiteLength)]],
      website2: ['', [Validators.maxLength(LIMITS.websiteLength)]],
    });
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTick'] && !changes['refreshTick'].firstChange) {
      this.load();
    }
  }

  private load(): void {
    this.loadingContent = true;
    this.channelHealthService.getProfile(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.applyProfile(response);
          this.loadingContent = false;
        },
        error: (err) => {
          this.loadingContent = false;
          this.alertService.error(err?.error?.message);
        },
      });
  }

  private applyProfile(response: ChannelBusinessProfileResponse): void {
    const profile = response?.data?.[0] ?? {};
    this.photoUrl = profile.profile_picture_url;
    this.form.patchValue({
      about: profile.about ?? '',
      description: profile.description ?? '',
      address: profile.address ?? '',
      email: profile.email ?? '',
      vertical: profile.vertical ?? '',
      website1: profile.websites?.[0] ?? '',
      website2: profile.websites?.[1] ?? '',
    });
    this.form.markAsPristine();
  }

  /** System avatar source with the standard blank-image fallback. */
  photoSrc(): string {
    return this.photoUrl || UtilHelper.no_image;
  }

  lengthOf(control: string): number {
    return String(this.form.get(control)?.value ?? '').length;
  }

  /** RF-32: save to the provider; the re-read below is what Meta actually stored. */
  save(): void {
    if (this.form.invalid || this.saving) return;
    this.saving = true;
    const value = this.form.value;
    const websites = [value.website1, value.website2].map((site: string) => (site ?? '').trim()).filter(Boolean);
    this.channelHealthService.updateProfile(this.channel._id, {
      about: value.about ?? '',
      description: value.description ?? '',
      address: value.address ?? '',
      email: value.email ?? '',
      vertical: value.vertical || undefined,
      websites,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving = false;
          this.applyProfile(response);
          this.alertService.success(this.t('channel.health.profile.saved'));
        },
        error: (err) => {
          // AC-29: the form keeps the edits — nothing is reset here.
          this.saving = false;
          this.alertService.error(err?.error?.message);
        },
      });
  }

  // ---------------------------------------------------------------- photo (RF-33)

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.alertService.error(this.t('channel.health.profile.photoNotImage'));
      return;
    }

    this.uploadingPhoto = true;
    this.cropToSquareJpeg(file)
      .then((blob) => {
        this.channelHealthService.updateProfilePhoto(this.channel._id, blob)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.uploadingPhoto = false;
              this.applyProfile(response);
              this.alertService.success(this.t('channel.health.profile.photoSaved'));
            },
            error: (err) => {
              this.uploadingPhoto = false;
              this.alertService.error(err?.error?.message);
            },
          });
      })
      .catch(() => {
        this.uploadingPhoto = false;
        this.alertService.error(this.t('channel.health.profile.photoCropError'));
      });
  }

  /** Center square crop → 640×640 JPEG, as the provider expects (RF-33). */
  private cropToSquareJpeg(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        const side = Math.min(image.width, image.height);
        const sx = (image.width - side) / 2;
        const sy = (image.height - side) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = PHOTO_TARGET_SIZE;
        canvas.height = PHOTO_TARGET_SIZE;
        const context = canvas.getContext('2d');
        if (!context) return reject(new Error('canvas'));
        context.drawImage(image, sx, sy, side, side, 0, 0, PHOTO_TARGET_SIZE, PHOTO_TARGET_SIZE);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('toBlob')),
          'image/jpeg',
          0.9,
        );
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('load'));
      };
      image.src = url;
    });
  }

  // ---------------------------------------------------------------- display name (RF-34)

  nameStatus(): string {
    return (this.channel?.health?.signals?.nameStatus ?? '').toUpperCase();
  }

  newNameStatus(): string {
    return (this.channel?.health?.signals?.newNameStatus ?? '').toUpperCase();
  }

  nameStatusLabel(): string {
    const status = this.nameStatus();
    const businessVerification = (this.channel?.health?.signals?.businessVerificationStatus ?? '').toUpperCase();
    if (status === 'AVAILABLE_WITHOUT_REVIEW' && businessVerification !== 'VERIFIED') {
      return this.t('channel.health.nameStatus.AVAILABLE_WITHOUT_REVIEW_UNVERIFIED');
    }
    const known = ['APPROVED', 'PENDING_REVIEW', 'DECLINED', 'EXPIRED', 'AVAILABLE_WITHOUT_REVIEW', 'NONE'];
    return known.includes(status) ? this.t(`channel.health.nameStatus.${status}`) : (status || '—');
  }

  /** A pending review of a requested new name (RF-34). */
  hasNameReviewPending(): boolean {
    return this.newNameStatus() === 'PENDING_REVIEW' || this.nameStatus() === 'PENDING_REVIEW';
  }

  requestDisplayName(): void {
    const name = this.newDisplayName?.trim();
    if (!name || this.requestingName) return;

    // Deliberate confirmation — the request triggers a real review at the provider.
    const dialogRef = this.dialog.open(ConfirmationComponent, { width: '500px' });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (Boolean(confirmed) !== true) return;
      this.requestingName = true;
      this.channelHealthService.requestDisplayName(this.channel._id, name)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.requestingName = false;
            this.newDisplayName = '';
            this.alertService.success(this.t('channel.health.profile.nameRequested'));
          },
          error: (err) => {
            this.requestingName = false;
            this.alertService.error(err?.error?.message);
          },
        });
    });
  }

  // ---------------------------------------------------------------- read-only blocks (RF-35)

  codeVerificationLabel(): string {
    const status = (this.channel?.health?.signals?.codeVerificationStatus ?? '').toUpperCase();
    const known = ['VERIFIED', 'EXPIRED', 'NOT_VERIFIED', 'PENDING'];
    return known.includes(status) ? this.t(`channel.health.codeVerification.${status}`) : (status || '—');
  }

  obaStatus(): string {
    return (this.channel?.health?.signals?.obaStatus ?? '').toUpperCase();
  }

  obaLabel(): string {
    const status = this.obaStatus();
    if (!status) return this.t('channel.health.profile.obaNone');
    const known = ['NOT_STARTED', 'IN_REVIEW', 'PENDING', 'APPROVED', 'REJECTED', 'DECLINED'];
    return known.includes(status) ? this.t(`channel.health.oba.${status}`) : status;
  }

  vertical(): string {
    return this.form.get('vertical')?.value || '';
  }
}
